import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { ChevronLeft, ChevronRight, Download, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DISARM_MS,
  FULL_VIEW_MAX_BYTES,
  formatBytes,
} from "./attachmentFormat";
import { displayName, relativeTime } from "./userDisplay";

/**
 * One photo at a time, over the task dialog: the thumbnail at once, the
 * original when it's small enough to be worth it, ←/→ to step through the
 * task's photos, Esc to close.
 *
 * Opens at the size it stays (board #98): the picture frame has a fixed
 * height from the first frame, so neither the dialog nor the picture grows
 * when the original replaces the 480 px thumbnail, or when stepping to a
 * photo of another shape. With an original on its way, the thumbnail is
 * drawn at the frame's size and the original lands in the same box; a
 * photo that only ever shows its thumbnail keeps it at its natural size. Remove lives here for photos (the grid
 * tiles are just pictures), with the same two-step confirm as the list.
 */
function PhotoViewer({
  photos,
  index,
  onIndexChange,
  onClose,
  onReturnFocus,
  mayRemove,
  onRemove,
  removing,
  removeError,
}) {
  const photo = photos[index];
  const [confirming, setConfirming] = useState(false);
  const [fullLoaded, setFullLoaded] = useState(null);
  const [fullFailed, setFullFailed] = useState(() => new Set());
  // The first original URL each photo was shown from, for as long as the
  // viewer is open: every re-list mints a new download link, and swapping
  // an <img src> would pull the original again (or break a loaded one).
  const fullSrc = useRef(new Map());
  const keepRef = useRef(null);
  const removeRef = useRef(null);

  // A confirm armed for one photo never carries over to the next.
  useEffect(() => {
    setConfirming(false);
  }, [photo?.id]);

  // Armed: focus Keep, and disarm by itself after a few seconds.
  useEffect(() => {
    if (!confirming || removing) return undefined;
    keepRef.current?.focus();
    const timer = setTimeout(() => setConfirming(false), DISARM_MS);
    return () => clearTimeout(timer);
  }, [confirming, removing]);

  if (!photo) return null;
  const many = photos.length > 1;
  const step = (delta) =>
    onIndexChange((index + delta + photos.length) % photos.length);
  // Only originals a browser can show (not e.g. HEIC), small enough to be
  // worth it, and not already failed.
  const showFull =
    photo.previewable &&
    photo.size <= FULL_VIEW_MAX_BYTES &&
    !fullFailed.has(photo.id);
  if (showFull && !fullSrc.current.has(photo.id))
    fullSrc.current.set(photo.id, photo.download_url);
  const disarm = () => {
    setConfirming(false);
    removeRef.current?.focus();
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        // No zoom-in on open: the viewer appears at its size.
        className="max-h-[calc(100dvh-2rem)] gap-3 sm:max-w-3xl data-[state=open]:zoom-in-100"
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          onReturnFocus();
        }}
        onKeyDown={(event) => {
          if (!many) return;
          if (event.key === "ArrowLeft") {
            event.preventDefault();
            step(-1);
          } else if (event.key === "ArrowRight") {
            event.preventDefault();
            step(1);
          }
        }}
      >
        <DialogTitle className="truncate pr-8 text-base">
          {photo.name}
        </DialogTitle>
        <DialogDescription className="text-xs">
          {many && `${index + 1} of ${photos.length} · `}
          {formatBytes(photo.size)}
          {photo.uploaded_by && ` · ${displayName(photo.uploaded_by)}`}
          {" · "}
          {relativeTime(photo.created_at)}
          {many && (
            // Keyboard hint: not on phones, where the buttons are the way.
            <span className="hidden sm:inline"> · ← → to step through</span>
          )}
        </DialogDescription>

        <div
          className="relative flex h-[min(60dvh,40rem)] items-center justify-center overflow-hidden rounded-md bg-muted"
          data-testid="photo-frame"
        >
          <img
            key={`thumb-${photo.id}`}
            src={photo.thumbnail_url}
            alt={photo.name}
            referrerPolicy="no-referrer"
            className={
              showFull
                ? "absolute inset-0 h-full w-full object-contain"
                : "max-h-full max-w-full object-contain"
            }
            hidden={fullLoaded === photo.id}
          />
          {showFull && (
            <img
              key={`full-${photo.id}`}
              src={fullSrc.current.get(photo.id)}
              alt={fullLoaded === photo.id ? photo.name : ""}
              referrerPolicy="no-referrer"
              className="absolute inset-0 h-full w-full object-contain"
              hidden={fullLoaded !== photo.id}
              onLoad={() => setFullLoaded(photo.id)}
              onError={() => {
                // Back to the thumbnail rather than a broken image.
                setFullLoaded(null);
                setFullFailed((prev) => new Set(prev).add(photo.id));
              }}
            />
          )}
          {many && (
            <>
              <Button
                type="button"
                size="icon"
                variant="secondary"
                className="absolute top-1/2 left-2 -translate-y-1/2 opacity-90"
                aria-label="Previous photo"
                onClick={() => step(-1)}
              >
                <ChevronLeft className="size-5" />
              </Button>
              <Button
                type="button"
                size="icon"
                variant="secondary"
                className="absolute top-1/2 right-2 -translate-y-1/2 opacity-90"
                aria-label="Next photo"
                onClick={() => step(1)}
              >
                <ChevronRight className="size-5" />
              </Button>
            </>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2">
          <Button asChild variant="outline" size="sm">
            <a
              href={photo.download_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Download className="size-4" />
              Download original
            </a>
          </Button>
          {mayRemove(photo) &&
            (confirming ? (
              <div className="flex items-center gap-1">
                <span className="text-xs text-muted-foreground">
                  Remove for everyone?
                </span>
                <Button
                  type="button"
                  size="sm"
                  variant="destructive"
                  disabled={removing}
                  onClick={() => onRemove(photo)}
                >
                  {removing ? "Removing…" : "Remove"}
                </Button>
                <Button
                  ref={keepRef}
                  type="button"
                  size="sm"
                  variant="ghost"
                  disabled={removing}
                  onClick={disarm}
                >
                  Keep
                </Button>
              </div>
            ) : (
              <Button
                ref={removeRef}
                type="button"
                size="sm"
                variant="ghost"
                aria-label={`Remove ${photo.name}`}
                onClick={() => setConfirming(true)}
              >
                <Trash2 className="size-4" />
                Remove
              </Button>
            ))}
        </div>
        {removeError && (
          <p role="alert" className="text-sm text-destructive">
            {removeError}
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}

const photoShape = PropTypes.shape({
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  size: PropTypes.number.isRequired,
  created_at: PropTypes.string.isRequired,
  download_url: PropTypes.string.isRequired,
  thumbnail_url: PropTypes.string.isRequired,
  previewable: PropTypes.bool,
  uploaded_by: PropTypes.shape({ username: PropTypes.string }),
});

PhotoViewer.propTypes = {
  photos: PropTypes.arrayOf(photoShape).isRequired,
  index: PropTypes.number.isRequired,
  onIndexChange: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onReturnFocus: PropTypes.func.isRequired,
  mayRemove: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  removing: PropTypes.bool,
  removeError: PropTypes.string,
};

export default PhotoViewer;
